/**
 * Emplacements CampPitch — tag tarif + camping parent + photos.
 */

import { useCallback, useEffect, useState } from 'react'
import { MapPicker, type MapPickerPoint } from './MapPicker'
import { existingPointsOf } from './mapPickerPoints'
import { EntityPhotosStrip } from './EntityPhotosStrip'
import { useOrgSiteMapView } from '../hooks/useOrgSiteMapView'
import {
  createOrganizationPitch,
  deleteOrganizationPitch,
  deleteOrganizationPitchPhoto,
  fetchOrganizationPitches,
  uploadOrganizationPitchPhoto,
  type OrganizationPitch,
  type RateTagOption,
  HttpError,
} from '../services/organizationApi'
import './OrganizationFacilitiesEditor.css'
import { useTranslation } from 'react-i18next'

type Props = {
  organizationId: number
  defaultCenter?: [number, number]
}

export function OrganizationPitchesEditor({ organizationId, defaultCenter }: Props) {
  const { t: tr } = useTranslation('organizations')
  const { siteView, mapKey, campings } = useOrgSiteMapView(organizationId, defaultCenter)
  const [items, setItems] = useState<OrganizationPitch[]>([])
  const [rateTags, setRateTags] = useState<RateTagOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [rateTag, setRateTag] = useState('1_service')
  const [campingId, setCampingId] = useState<string>('')
  const [point, setPoint] = useState<MapPickerPoint | null>(null)
  const [saving, setSaving] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchOrganizationPitches(organizationId)
      setItems(data.results)
      setRateTags(data.rate_tags || [])
      if (data.rate_tags?.length && !data.rate_tags.some((t) => t.slug === rateTag)) {
        setRateTag(data.rate_tags[0].slug)
      }
    } catch (err) {
      setError(err instanceof HttpError ? err.message : tr('OrganizationPitchesEditor.chargement_impossible'))
    } finally {
      setLoading(false)
    }
  }, [organizationId, rateTag])

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId])

  const campingName = (id: number | null) => {
    if (id == null) return null
    const c = campings.find((x) => x.id === id)
    return c?.name || `#${id}`
  }

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!point) {
      setError(tr('OrganizationPitchesEditor.cliquez_carte_positionner_emplacement'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      await createOrganizationPitch(organizationId, {
        name: name.trim() || undefined,
        rate_tag: rateTag || undefined,
        camping_id: campingId ? Number(campingId) : null,
        location: point,
      })
      setName('')
      setPoint(null)
      await reload()
    } catch (err) {
      setError(err instanceof HttpError ? err.message : tr('OrganizationPitchesEditor.enregistrement_impossible'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm(tr('OrganizationPitchesEditor.supprimer_cet_emplacement'))) return
    try {
      await deleteOrganizationPitch(organizationId, id)
      await reload()
    } catch (err) {
      setError(err instanceof HttpError ? err.message : tr('OrganizationPitchesEditor.suppression_impossible'))
    }
  }

  return (
    <section className="org-facilities">
      <header className="org-facilities__header">
        <div>
          <h2 className="org-facilities__title">{tr('OrganizationPitchesEditor.emplacements')}</h2>
          <p className="org-facilities__hint">
            {tr('OrganizationPitchesEditor.camp_pitches_point_tag_tarif')}
          </p>
        </div>
      </header>

      {error && (
        <p className="org-facilities__error" role="alert">
          {error}
        </p>
      )}

      <div className="org-facilities__list-block">
        {loading ? (
          <p className="org-facilities__muted">{tr('OrganizationPitchesEditor.chargement')}</p>
        ) : items.length === 0 ? (
          <p className="org-facilities__muted org-facilities__empty">{tr('OrganizationPitchesEditor.aucun_emplacement')}</p>
        ) : (
          <ul className="org-facilities__list org-facilities__list--tall">
            {items.map((p) => (
              <li key={p.id} className="org-facilities__item org-facilities__item--stack">
                <div className="org-facilities__item-head">
                  <div>
                    <strong>{p.name || p.rate_tag_label || p.rate_tag || tr('OrganizationPitchesEditor.pitch', { id: p.id })}</strong>
                    {p.rate_tag_label || p.rate_tag ? (
                      <span> — {p.rate_tag_label || p.rate_tag}</span>
                    ) : null}
                    {p.camping_id != null && (
                      <span className="org-facilities__coords">
                        {' '}
                        {tr('OrganizationPitchesEditor.camping', { camping_id: campingName(p.camping_id) ?? '' })}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="org-facilities__delete"
                    onClick={() => void handleDelete(p.id)}
                  >
                    {tr('OrganizationPitchesEditor.supprimer')}
                  </button>
                </div>
                <EntityPhotosStrip
                  photos={p.photos || []}
                  onUpload={async (file) => {
                    await uploadOrganizationPitchPhoto(organizationId, p.id, { image: file })
                    await reload()
                  }}
                  onDelete={async (photoId) => {
                    await deleteOrganizationPitchPhoto(organizationId, p.id, photoId)
                    await reload()
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <form className="org-facilities__toolbar" onSubmit={(e) => void handleAdd(e)}>
        <label className="org-facilities__field org-facilities__field--grow">
          <span>{tr('OrganizationPitchesEditor.nom')}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={tr('OrganizationPitchesEditor.ex_12')}
          />
        </label>
        <label className="org-facilities__field">
          <span>{tr('OrganizationPitchesEditor.tag_tarif')}</span>
          <select value={rateTag} onChange={(e) => setRateTag(e.target.value)}>
            {rateTags.map((t) => (
              <option key={t.slug} value={t.slug}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="org-facilities__field">
          <span>{tr('OrganizationPitchesEditor.camping_parent')}</span>
          <select value={campingId} onChange={(e) => setCampingId(e.target.value)}>
            <option value="">{tr('OrganizationPitchesEditor.aucun')}</option>
            {campings.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <div className="org-facilities__point-status" aria-live="polite">
          {point ? (
            <span>
              {tr('OrganizationPitchesEditor.point', { lat: point.lat.toFixed(5), lng: point.lng.toFixed(5) })}
            </span>
          ) : (
            <span className="org-facilities__muted">{tr('OrganizationPitchesEditor.aucun_point')}</span>
          )}
        </div>
        <button type="submit" disabled={saving || !point} className="org-facilities__submit">
          {saving ? tr('OrganizationPitchesEditor.enregistrement') : tr('OrganizationPitchesEditor.ajouter_emplacement')}
        </button>
      </form>

      <div className="org-facilities__map">
        <MapPicker
          key={`pitch-${mapKey}`}
          value={point}
          onChange={setPoint}
          defaultCenter={siteView.center}
          defaultZoom={siteView.zoom}
          fitBounds={siteView.fitBounds}
          height="min(420px, 50vh)"
          showCoordinateFields={false}
          existingPoints={existingPointsOf(items, (p) => p.name || p.rate_tag_label || p.rate_tag)}
        />
      </div>
    </section>
  )
}
